
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TripMemberProfile {
  username?: string;
  avatar_url?: string;
  email?: string;
}

export interface TripMember {
  id: string;
  user_id: string;
  trip_id: string;
  created_at: string;
  is_owner?: boolean;
  profiles?: TripMemberProfile | null;
}

export const useTripMembers = (tripId?: string) => {
  const [members, setMembers] = useState<TripMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTripMembers = async () => {
    if (!tripId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First fetch trip owner information
      const { data: tripData, error: tripError } = await supabase
        .from('trips')
        .select('owner_id')
        .eq('id', tripId)
        .single();
        
      if (tripError) throw tripError;
      
      const ownerId = tripData?.owner_id;
      
      // Updated approach: Fetch members first, then profiles separately
      const { data: membersData, error: membersError } = await supabase
        .from('trip_members')
        .select('*')
        .eq('trip_id', tripId);
        
      if (membersError) throw membersError;
      
      // Collect all user_ids to fetch their profiles
      const userIds = membersData?.map(member => member.user_id) || [];
      
      // Add owner ID if not already in the list
      if (ownerId && !userIds.includes(ownerId)) {
        userIds.push(ownerId);
      }
      
      // Fetch profiles for all users if there are any IDs to fetch
      const profilesMap: Record<string, TripMemberProfile> = {};
      
      if (userIds.length > 0) {
        // Modified query - only select columns that exist in the profiles table
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, avatar_url')
          .in('id', userIds);
          
        if (profilesError && profilesError.code !== 'PGRST116') {
          console.error('Error fetching profiles:', profilesError);
        }
        
        // Create a map of user_id to profile data for easy lookup
        if (profilesData) {
          profilesData.forEach(profile => {
            profilesMap[profile.id] = {
              username: profile.username || 'Unknown Member',
              avatar_url: profile.avatar_url,
              // email field is removed from the selection since it doesn't exist
            };
          });
        }
      }
      
      // Prepare the final members list
      const allMembers: TripMember[] = [];
      
      // Add the owner first if available
      if (ownerId) {
        const ownerProfile = profilesMap[ownerId] || { username: 'Trip Owner' };
        
        allMembers.push({
          id: 'owner',
          user_id: ownerId,
          trip_id: tripId,
          created_at: '',
          is_owner: true,
          profiles: ownerProfile
        });
      }
      
      // Then add all members
      if (membersData) {
        membersData.forEach(member => {
          // Skip if this is the owner (already added)
          if (member.user_id === ownerId) return;
          
          const memberProfile = profilesMap[member.user_id] || { username: 'Unknown Member' };
          
          allMembers.push({
            ...member,
            profiles: memberProfile
          });
        });
      }
      
      setMembers(allMembers);
    } catch (error: any) {
      console.error('Error fetching trip members:', error);
      setError(error.message || 'Failed to load trip members');
      toast.error('Failed to load trip members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tripId) {
      fetchTripMembers();
    }
  }, [tripId]);

  return {
    members,
    isLoading,
    error,
    fetchTripMembers
  };
};
